/**
 * Settings → Billing.
 *
 * Раскладка и стиль — из макета `billing-design-v2` (`settings_billing.jsx`),
 * данные — из `GET /api/orgs/{id}/billing`.
 *
 * ★ Ни одной бизнес-величины в этом файле.
 *
 * Суммы, даты, названия тарифов и все фразы, внутри которых они стоят,
 * приходят с сервера готовыми строками. В макете, например, Ultimate стоит
 * $899, а утверждённая цена — $999: цифра, зашитая в разметку, разошлась бы
 * с тем, за что клиента списывают, и заметил бы это он.
 *
 * По той же причине здесь нет ни «$12,000», ни «дня N из 3»: окно на
 * исправление объявлено клиенту трёхдневным, фактическое отключение
 * наступает позже, и это внутреннее число не должно иметь пути на экран.
 *
 * Кнопки действий рисуются по флагам `actions` с сервера. Отмена, смена
 * плана и оплата долга появятся вместе со своими эндпоинтами — кнопка,
 * которой некуда вести, хуже отсутствующей.
 */

import { useState } from "react";
import { Badge } from "@/components/Badge";
import { Btn } from "@/components/Btn";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { I } from "@/components/Icon";
import { useAuth } from "@/lib/useAuth";
import {
  useCancelSubscription,
  useChangePlan,
  useInvoicePdf,
  useOrgBilling,
  useResumeSubscription,
  useStartCheckout,
} from "@/lib/api-hooks";
import { PlanPicker } from "./PlanPicker";
import type { BillingOverview } from "@/lib/types";
import { StSectionHead } from "./_shared";

function Num({
  children,
  size,
  weight,
  color,
}: {
  children: React.ReactNode;
  size?: number;
  weight?: number;
  color?: string;
}) {
  return (
    <span
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: size ?? "inherit",
        fontWeight: weight ?? 500,
        color: color ?? "inherit",
        letterSpacing: "-0.01em",
      }}
    >
      {children}
    </span>
  );
}

/** Статус подписки. Незнакомый код показываем как есть — чужой словарь
 *  может пополниться, и «—» на экране хуже сырого слова. */
function StatusBadge({ code, label }: { code: string; label: string }) {
  const kind =
    code === "active"
      ? "enabled"
      : code === "trialing"
        ? "accent"
        : code === "past_due"
          ? "warn"
          : code === "canceled"
            ? "paused"
            : "neutral";
  return (
    <Badge kind={kind as never}>
      <span className="dot" />
      {label}
    </Badge>
  );
}

function Banner({ b }: { b: NonNullable<BillingOverview["banner"]> }) {
  const neg = b.stage === "disabled" || b.stage === "grace";
  const t = neg
    ? { bg: "var(--c-neg-50)", bd: "#f6cfcf", ink: "var(--c-neg-600)" }
    : { bg: "var(--c-warn-50)", bd: "#f0dcb0", ink: "#8a5a04" };
  return (
    <div
      style={{
        background: t.bg,
        border: `1px solid ${t.bd}`,
        borderRadius: "var(--r-lg)",
        padding: "14px 16px",
        display: "flex",
        gap: 14,
        alignItems: "flex-start",
        marginBottom: 16,
      }}
    >
      <span
        style={{
          width: 20,
          height: 20,
          borderRadius: "50%",
          background: t.ink,
          color: "#fff",
          display: "grid",
          placeItems: "center",
          fontSize: 12,
          fontWeight: 700,
          flex: "0 0 auto",
          marginTop: 1,
        }}
      >
        !
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: t.ink }}>
          {b.title}
        </div>
        <div
          style={{
            fontSize: 12.5,
            color: "var(--c-text-2)",
            marginTop: 4,
            lineHeight: 1.55,
          }}
        >
          {b.text}
        </div>
      </div>
    </div>
  );
}

function Row({
  label,
  children,
  top,
}: {
  label: string;
  children: React.ReactNode;
  top?: boolean;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "190px 1fr",
        gap: 16,
        alignItems: top ? "flex-start" : "center",
        padding: "11px 0",
        borderTop: "1px solid var(--c-divider)",
      }}
    >
      <div style={{ fontSize: 12.5, color: "var(--c-text-3)" }}>{label}</div>
      <div style={{ fontSize: 13.5, color: "var(--c-text)" }}>{children}</div>
    </div>
  );
}

function PlanCard({
  d,
  onCancel,
  onResume,
  onChangePlan,
  busy,
}: {
  d: BillingOverview;
  onCancel: () => void;
  onResume: () => void;
  onChangePlan: () => void;
  busy: boolean;
}) {
  return (
    <div className="card card-pad" style={{ marginBottom: 16 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 20,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 12,
              color: "var(--c-text-3)",
              fontWeight: 500,
              marginBottom: 6,
            }}
          >
            Current plan
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h3
              style={{
                margin: 0,
                fontSize: 20,
                fontWeight: 600,
                letterSpacing: "-0.015em",
              }}
            >
              {d.plan ? d.plan.label : "No plan"}
            </h3>
            {d.status && (
              <StatusBadge code={d.status.code} label={d.status.label} />
            )}
          </div>
          <div style={{ marginTop: 10, fontSize: 15 }}>
            {d.plan ? (
              <Num size={19} weight={500}>
                {d.plan.price_text}
              </Num>
            ) : (
              <span style={{ color: "var(--c-text-2)" }}>{d.spend.text ?? ""}</span>
            )}
          </div>
        </div>

        {/* Кнопки — по флагам с сервера. Он знает и статус подписки, и роль
            запросившего, и считает их ВМЕСТЕ; своя проверка роли рядом
            была бы вторым определением того же правила, и разошлись бы
            они молча — на деньгах. */}
        {(d.actions.can_cancel ||
          d.actions.can_resume ||
          d.actions.can_change_plan ||
          d.actions.can_subscribe ||
          d.plan_change?.support_email) && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 10,
            }}
          >
            {d.actions.can_resume && (
              <Btn variant="primary" onClick={onResume} disabled={busy}>
                Keep subscription
              </Btn>
            )}
            {(d.actions.can_change_plan || d.actions.can_subscribe) && (
              <Btn variant="primary" onClick={onChangePlan} disabled={busy}>
                {d.actions.can_subscribe ? "Choose a plan" : "Change plan"}
              </Btn>
            )}
            {/* ★ На триале кнопки НЕТ вовсе — Paddle не даёт менять
                позиции триальной подписки, а обход списал бы старый тариф
                целиком и сжёг остаток триала. Вместо неё письмо, и адрес
                приходит с сервера. */}
            {d.plan_change?.support_email && (
              <a
                href={`mailto:${d.plan_change.support_email}?subject=${encodeURIComponent(
                  "Plan change request",
                )}`}
                style={{ fontSize: 12.5 }}
              >
                Mail us to change plan
              </a>
            )}
            {d.actions.can_cancel && (
              <button
                onClick={onCancel}
                disabled={busy}
                style={{
                  background: "none",
                  border: 0,
                  padding: 0,
                  cursor: busy ? "default" : "pointer",
                  fontSize: 12.5,
                  color: "var(--c-text-3)",
                  textDecoration: "underline",
                  textUnderlineOffset: 3,
                }}
              >
                Cancel subscription
              </button>
            )}
          </div>
        )}
      </div>

      <div style={{ marginTop: 18 }}>
        {d.trial && <Row label="Trial">{d.trial.text}</Row>}
        {d.period && <Row label="Current period">{d.period.text}</Row>}
        {d.commission && (
          <Row label="Usage fee so far" top>
            <div>
              <Num size={17} weight={500}>
                {d.commission.accrued.text}
              </Num>
              <div
                className="muted"
                style={{ fontSize: 12.5, marginTop: 6, lineHeight: 1.6 }}
              >
                {d.commission.text}
              </div>
            </div>
          </Row>
        )}
        <Row label="Billing details">
          <span
            style={{
              fontSize: 13,
              color: d.profile.ready ? "var(--c-text)" : "var(--c-warn-600, #8a5a04)",
            }}
          >
            {d.profile.text}
          </span>
        </Row>
      </div>
    </div>
  );
}

function AllowanceCard({ d }: { d: BillingOverview }) {
  // Платящему сервер лимита не присылает — карточки просто нет.
  // Разбираем всё разом: одна проверка сужает типы всех полей, и ни одно
  // место ниже не приходится подпирать по отдельности.
  const { percent: pct, consumed, limit, remaining } = d.spend;
  if (
    pct === undefined || !consumed || !limit || !remaining
  ) {
    return null;
  }
  const color =
    pct >= 100
      ? "var(--c-neg-500)"
      : pct >= 80
        ? "#d99a1f"
        : "var(--c-accent-600)";
  return (
    <div className="card card-pad" style={{ marginBottom: 16 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: 16,
          marginBottom: 12,
        }}
      >
        <div>
          <div style={{ fontSize: 13.5, fontWeight: 600 }}>Free allowance</div>
          <div className="muted" style={{ fontSize: 12.5, marginTop: 4 }}>
            Ad spend managed through OneAds since the account was created.
          </div>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 8,
          marginBottom: 10,
        }}
      >
        <Num size={22} weight={500}>
          {consumed.text}
        </Num>
        <span style={{ fontSize: 13, color: "var(--c-text-3)" }}>of</span>
        <Num size={15} color="var(--c-text-3)">
          {limit.text}
        </Num>
        <span style={{ fontSize: 13, color: "var(--c-text-3)" }}>used</span>
      </div>

      <div
        style={{
          height: 8,
          borderRadius: 999,
          background: "var(--c-surface-sunk)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            borderRadius: 999,
            background: color,
            transition: "width .3s var(--ease)",
          }}
        />
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: 10,
          fontSize: 12.5,
          color: "var(--c-text-3)",
        }}
      >
        <span>
          <Num
            color={pct >= 100 ? "var(--c-neg-600)" : "var(--c-text-2)"}
          >
            {remaining.text}
          </Num>{" "}
          remaining
        </span>
        <Num color="var(--c-text-4)">{pct}%</Num>
      </div>
    </div>
  );
}

function InvoicesCard({
  d,
  orgId,
}: {
  d: BillingOverview;
  orgId: number | null;
}) {
  const pdf = useInvoicePdf();
  const [wanted, setWanted] = useState<string | null>(null);

  // Окно открывается ПОСЛЕ ответа, поэтому браузер может счесть его
  // всплывающим и заблокировать. Тогда показываем ссылку прямо в строке —
  // молчаливое «ничего не произошло» человек читает как поломку.
  const [blocked, setBlocked] = useState<{ id: string; url: string } | null>(
    null,
  );

  function open(id: string) {
    if (!orgId) return;
    setWanted(id);
    setBlocked(null);
    pdf.mutate(
      { orgId, transactionId: id },
      {
        onSuccess: (url) => {
          const w = window.open(url, "_blank", "noopener,noreferrer");
          if (!w) setBlocked({ id, url });
        },
        onSettled: () => setWanted(null),
      },
    );
  }

  return (
    <div className="card">
      <div className="card-head">
        <h3>Invoices</h3>
      </div>
      {d.invoices.length === 0 ? (
        <div style={{ padding: "44px 20px", textAlign: "center" }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: "var(--c-surface-sunk)",
              display: "grid",
              placeItems: "center",
              color: "var(--c-text-3)",
              margin: "0 auto 12px",
            }}
          >
            {I.list}
          </div>
          <div style={{ fontSize: 13.5, fontWeight: 500 }}>No invoices yet</div>
          <div className="muted" style={{ fontSize: 12.5, marginTop: 4 }}>
            Invoices appear here after your first charge.
          </div>
        </div>
      ) : (
        <table className="dt fit" style={{ minWidth: 0 }}>
          <thead>
            <tr>
              <th>Date</th>
              <th>Invoice #</th>
              <th className="num">Amount</th>
              <th>Status</th>
              <th style={{ width: 110, textAlign: "right" }}>Invoice</th>
            </tr>
          </thead>
          <tbody>
            {d.invoices.map((r) => (
              <tr key={r.id}>
                <td className="mono">{r.date_text}</td>
                <td className="mono">{r.number ?? "—"}</td>
                <td className="num mono">
                  {r.total_text}
                  {/* Итог не отвечает, почему списали больше тарифа.
                      Строка приходит готовой: на фронте сумм нет. */}
                  {r.breakdown_text && (
                    <div
                      className="muted"
                      style={{ fontSize: 11.5, fontWeight: 400, marginTop: 2 }}
                    >
                      {r.breakdown_text}
                    </div>
                  )}
                </td>
                <td>
                  {r.status === "completed" ? (
                    <Badge kind="enabled">
                      <span className="dot" />
                      Paid
                    </Badge>
                  ) : (
                    <Badge kind="paused">{r.status}</Badge>
                  )}
                </td>
                <td style={{ textAlign: "right" }}>
                  {/* Кнопки нет у невыписанного счёта: документа не
                      существует, и она вела бы в отказ. */}
                  {r.has_pdf &&
                    (blocked?.id === r.id ? (
                      <a
                        href={blocked.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: 12.5 }}
                      >
                        Open PDF
                      </a>
                    ) : (
                      <button
                        className="btn sm"
                        style={{ height: 28 }}
                        disabled={wanted === r.id}
                        onClick={() => open(r.id)}
                      >
                        {wanted === r.id ? "…" : "PDF"}
                      </button>
                    ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function BillingTab() {
  const { user } = useAuth();
  const orgId = user?.org_id ?? null;
  const q = useOrgBilling(orgId);
  const cancel = useCancelSubscription();
  const resume = useResumeSubscription();
  const changePlan = useChangePlan();
  const checkout = useStartCheckout();
  const [asking, setAsking] = useState(false);
  const [picking, setPicking] = useState(false);
  const [planError, setPlanError] = useState<string | null>(null);
  const busy =
    cancel.isPending ||
    resume.isPending ||
    changePlan.isPending ||
    checkout.isPending;

  function pickPlan(plan: string) {
    if (!orgId) return;
    setPlanError(null);
    // Подписки нет — это покупка, и она идёт через страницу оплаты.
    // Подписка есть — это смена тарифа, её считает Paddle.
    const buying = q.data?.actions.can_subscribe;
    const run = buying
      ? checkout.mutateAsync({ orgId, plan }).then((r) => {
          window.location.assign(r.pay_url);
        })
      : changePlan.mutateAsync({ orgId, plan }).then(() => setPicking(false));
    run.catch((e: unknown) => {
      const msg =
        (e as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ??
        "Could not change the plan. Please try again.";
      setPlanError(msg);
    });
  }

  return (
    <>
      <StSectionHead
        title="Billing"
        desc="Plan, usage and invoices. Payments are processed by Paddle."
      />

      {q.isLoading && (
        <div className="card card-pad muted" style={{ fontSize: 13 }}>
          Loading…
        </div>
      )}

      {q.isError && (
        <div className="card card-pad" style={{ fontSize: 13 }}>
          Could not load billing right now. Refresh the page to try again.
        </div>
      )}

      {q.data && (
        <>
          {q.data.banner && <Banner b={q.data.banner} />}
          {/* ★ Долг платится по ссылке, и ведёт она на нашу же страницу
              оплаты: Paddle подставляет наш адрес по умолчанию. До 20.08
              страница такую ссылку отвергала, и человек, пришедший из
              письма Paddle, читал «этот чекаут уже закрыт». */}
          {q.data.debt && (
            <div
              style={{
                background: "var(--c-warn-50, #fdf6e6)",
                border: "1px solid var(--c-warn-600, #8a5a04)",
                borderRadius: "var(--r-lg)",
                padding: "14px 16px",
                display: "flex",
                gap: 14,
                alignItems: "center",
                marginBottom: 16,
              }}
            >
              <div style={{ flex: 1, minWidth: 0, fontSize: 13 }}>
                {q.data.debt.text}
              </div>
              <Btn
                variant="primary"
                onClick={() => window.location.assign(q.data!.debt!.pay_url)}
              >
                Update payment
              </Btn>
            </div>
          )}
          {q.data.cancellation && (
            <div
              style={{
                background: "var(--c-surface-sunk)",
                border: "1px solid var(--c-divider)",
                borderRadius: "var(--r-lg)",
                padding: "12px 16px",
                fontSize: 13,
                marginBottom: 16,
              }}
            >
              {q.data.cancellation.text}
            </div>
          )}
          <PlanCard
            d={q.data}
            busy={busy}
            onCancel={() => setAsking(true)}
            onResume={() => orgId && resume.mutate(orgId)}
            onChangePlan={() => {
              setPlanError(null);
              setPicking(true);
            }}
          />
          {picking && (
            <PlanPicker
              plans={q.data.available_plans}
              current={q.data.plan?.key ?? null}
              busy={busy}
              actionLabel={
                q.data.actions.can_subscribe ? "Choose" : "Switch to this plan"
              }
              onPick={pickPlan}
              onClose={() => setPicking(false)}
            />
          )}
          {planError && (
            <div
              className="card card-pad"
              style={{
                marginBottom: 16,
                fontSize: 13,
                border: "1px solid var(--c-neg, #b3261e)",
              }}
            >
              {planError}
            </div>
          )}
          <AllowanceCard d={q.data} />
          <InvoicesCard d={q.data} orgId={orgId} />

          {/* Три факта, а не один вопрос: клиент должен понимать, что теряет
              доступ не сейчас, что финальный счёт включит накопленную
              комиссию, и что будет после. */}
          <ConfirmDialog
            open={asking}
            destructive
            title="Cancel subscription?"
            confirmLabel="Cancel subscription"
            cancelLabel="Keep subscription"
            body={
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div>
                  {q.data.period
                    ? `You keep full access until the end of the paid period. ${q.data.period.text}.`
                    : "You keep full access until the end of the paid period."}
                </div>
                <div>
                  The final invoice will include the usage fee accrued in the
                  current period.
                </div>
                <div>
                  After that the organization moves back to the free allowance.
                </div>
              </div>
            }
            onCancel={() => setAsking(false)}
            onConfirm={() => {
              setAsking(false);
              if (orgId) cancel.mutate(orgId);
            }}
          />
        </>
      )}
    </>
  );
}
